import React, { useReducer } from "react";

import DialogContent from "@busbud/design-system-components/dist/DialogContent";
import Divider from "@busbud/design-system-components/dist/Divider";

import {
  isDynamicWhiteLabel,
  useWhitelabelContext
} from "@app/context/whitelabel-context";
import { PoweredByBusbud } from "@app/modules/home/components/powered-by-busbud";

import type { AnyAction } from "@reduxjs/toolkit";

import { useStyles } from "../authentication.styles";
import { ForgotPassword } from "../forgot-password";
import { SignIn, SignInFooter } from "../sign-in";
import { SignUp, SignUpFooter } from "../sign-up";
import { VerifyEmail } from "../verify-email";

type action =
  | "goToSignIn"
  | "goToForgotPassword"
  | "goToSignUp"
  | "goToResendAccountVerification";

export type ViewName = "SignIn" | "SignUp" | "ForgotPassword" | "VerifyEmail";

interface View {
  AuthenticationView: React.FC<any>;
  footer?: React.FC<any>;
  props: (dispatch: React.Dispatch<{ type: action; email?: string }>) => any;
  footer_props?: (
    dispatch: React.Dispatch<{ type: action; email?: string }>
  ) => any;
}

const ForgotPasswordView: View = {
  AuthenticationView: ForgotPassword,
  props: dispatch => ({
    goToForgotPassword: () => dispatch({ type: "goToForgotPassword" }),
    goToSignIn: () => dispatch({ type: "goToSignIn" })
  })
};

const SignInView: View = {
  AuthenticationView: SignIn,
  footer: SignInFooter,
  props: dispatch => ({
    goToForgotPassword: () => dispatch({ type: "goToForgotPassword" }),
    goToSignUp: (email?: string) => dispatch({ type: "goToSignUp", email }),
    goToResendAccountVerification: (email: string) =>
      dispatch({ type: "goToResendAccountVerification", email })
  }),
  footer_props: dispatch => ({
    goToSignUp: (email?: string) => dispatch({ type: "goToSignUp", email })
  })
};

const getVerifyEmailView = (email?: string): View => ({
  AuthenticationView: VerifyEmail,
  props: dispatch => ({
    email,
    goToSignIn: () => dispatch({ type: "goToSignIn" })
  })
});

const getSignUpView = (email?: string): View => ({
  AuthenticationView: SignUp,
  props: dispatch => ({
    email,
    goToSignIn: () => dispatch({ type: "goToSignIn" })
  }),
  footer: SignUpFooter,
  footer_props: dispatch => ({
    goToSignIn: () => dispatch({ type: "goToSignIn" })
  })
});

const reducer = (_state: unknown, action: AnyAction): View => {
  return getView(
    actionToViewMapper[action.type as keyof typeof actionToViewMapper],
    action.email
  );
};

const actionToViewMapper = {
  goToForgotPassword: "ForgotPassword",
  goToSignUp: "SignUp",
  goToResendAccountVerification: "VerifyEmail",
  goToSignIn: "SignIn"
} as const;

const getView = (view: ViewName, email?: string) => {
  switch (view) {
    case "ForgotPassword":
      return ForgotPasswordView;
    case "SignUp":
      return getSignUpView(email);
    case "VerifyEmail":
      return getVerifyEmailView(email);
    case "SignIn":
    default:
      return SignInView;
  }
};

interface Props {
  opening_view?: ViewName;
  redirect_url?: string;
  carousel_start_idx?: number;
  from_expired_link?: boolean;
}

export const AuthenticationViewsManager: React.FC<Props> = ({
  opening_view = "SignIn",
  ...other_props
}) => {
  const { classes } = useStyles();
  const { whitelabel } = useWhitelabelContext();

  const [
    { AuthenticationView, footer: Footer, footer_props = () => {}, props },
    dispatch
  ] = useReducer(reducer, getView(opening_view));

  return (
    <>
      <DialogContent className={classes.content}>
        <AuthenticationView {...props(dispatch)} {...other_props} />
      </DialogContent>
      {!!Footer && (
        <>
          <Divider />
          {!!whitelabel && isDynamicWhiteLabel(whitelabel) && (
            <PoweredByBusbud whitelabel_name={whitelabel?.name} />
          )}
          <div className={classes.footer}>
            <Footer {...footer_props(dispatch)} />
          </div>
        </>
      )}
    </>
  );
};
